/** Error boundaries: route-level error components and a widget-level boundary (docs/09). */
import { isApiError } from '@oex/contracts';
import { Alert, Button, EmptyState } from '@oex/ui';
import { TriangleAlert } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from './i18n';

export function ErrorView({ error, reset }: { error: unknown; reset?: () => void }) {
  const requestId = isApiError(error) ? error.problem.requestId : undefined;
  return (
    <div className="p-6">
      <EmptyState
        icon={TriangleAlert}
        title={t('app.error.title')}
        body={
          <>
            {t('app.error.body')}
            {requestId ? (
              <span className="font-data mt-2 block text-xs">
                {t('app.error.requestId', { id: requestId })}
              </span>
            ) : null}
          </>
        }
        action={
          reset ? (
            <Button variant="secondary" onClick={reset}>
              {t('app.retry')}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

type State = { error: unknown };

/** Keeps one broken widget from blanking the page. */
export class ErrorBoundary extends Component<{ children: ReactNode; compact?: boolean }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('widget error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return this.props.compact ? (
        <Alert tone="danger" title={t('app.error.title')}>
          <Button variant="link" onClick={() => this.setState({ error: null })}>
            {t('app.retry')}
          </Button>
        </Alert>
      ) : (
        <ErrorView error={this.state.error} reset={() => this.setState({ error: null })} />
      );
    }
    return this.props.children;
  }
}
